/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// THE DECISION CHECKS FOR AN INDIVIDUAL CLIENT (individual onboarding, piece 8,
// 21/10/2026). A company's checks ask for a registration number, owners,
// directors and a regulator, none of which a private individual has, so an
// individual's approval would have needed an override every time and an
// override that is routine stops meaning anything. These are the questions a
// person's file answers instead. reconcileCaseState (case-state.js) uses them
// in place of the company block for an individual case only; the sanctions
// check, the identity check and the exceptions stay as they are. Pure.
//
// Each check fails safe: a missing answer is open, never assumed. Form A is
// open until the client's Form A is confirmed, and is not resolvable away when
// the case is under the Swiss regime (the owner: every individual client under
// the Swiss regime signs it). A politically exposed person, declared or found
// by the client's screen, stays open until the officer records the senior
// management approval with Resolve (the Audit Record's PEP approval line).

import { tierInForce } from './tier-in-force.js';
import { applyCheckResolutions } from './board-checks.js';

const real = (v) => { const t = String(v == null ? '' : v).trim(); return (!t || /^[.…\s]+$/.test(t)) ? '' : t; };
const list = (a) => (Array.isArray(a) ? a : []).map(real).filter(Boolean);

/** The client's own screening record shows a PEP topic. */
export function screenedAsPep(partyScreenings, clientName, samePerson) {
  const same = typeof samePerson === 'function' ? samePerson : ((a, b) => String(a).toLowerCase() === String(b).toLowerCase());
  return (Array.isArray(partyScreenings) ? partyScreenings : []).some((s) => s && s.name && same(s.name, clientName)
    && ['hit', 'caution'].includes(s.status) && /role\.pep|\bpep\b/i.test(String(s.topics || '')));
}

/** Is a Form A for the client confirmed (Sumsub) or signed (legacy DeepSign)? */
export function clientFormAConfirmed(formRequests, clientName, samePerson) {
  const same = typeof samePerson === 'function' ? samePerson : ((a, b) => a === b);
  return (Array.isArray(formRequests) ? formRequests : []).some((f) => f && f.form_type === 'A' && same(f.signer_name, clientName)
    && ['confirmed', 'signed'].includes(String(f.status || '').toLowerCase()));
}

/**
 * @param r      the board (an individual's)
 * @param cd     collected_data
 * @param ctx    { checkRes, sofSatisfactory, formRequests (rows, or undefined when this view did not read them),
 *                 formAState ('required' | 'not_required' | 'unknown', or undefined), features, samePerson }
 * @returns checks [{ key, q, detail, done, resolvable }]
 */
export function individualDecisionChecks(r = {}, cd = {}, ctx = {}) {
  const cp = (r.clientPerson && typeof r.clientPerson === 'object') ? r.clientPerson : {};
  const name = real(cp.fullName) || real(r.clientName) || 'the client';
  const checks = [];

  const missing = [['date of birth', cp.dateOfBirth], ['nationality', cp.nationality], ['country of residence', cp.countryOfResidence], ['residential address', cp.residentialAddress]].filter(([, v]) => !real(v)).map(([k]) => k);
  checks.push({
    key: 'person', q: 'Who is the client?', resolvable: true,
    detail: missing.length ? `${name}: not on file: ${missing.join(', ')}` : `${name} · born ${real(cp.dateOfBirth)} · ${[real(cp.nationality), ...list(cp.otherNationalities)].join(' and ')} · lives in ${real(cp.countryOfResidence)}`,
    done: !!real(cp.fullName) && missing.length === 0,
  });

  const pd = (cp.pepDeclaration && typeof cp.pepDeclaration === 'object') ? cp.pepDeclaration : {};
  const declared = typeof pd.isPep === 'boolean';
  const declaredPep = pd.isPep === true || pd.familyOrAssociateIsPep === true;
  const screenedPep = screenedAsPep(cd.partyScreenings, name, ctx.samePerson);
  const pepWhat = [pd.isPep === true ? 'declared a politically exposed person' : null, pd.familyOrAssociateIsPep === true ? 'declared a family member or close associate of one' : null, screenedPep ? 'the screen found a politically exposed person record' : null].filter(Boolean).join('; ');
  const pepRole = [real(pd.role), real(pd.country)].filter(Boolean).join(', ');
  checks.push({
    key: 'pep', q: 'Is the client a politically exposed person?', resolvable: true,
    detail: (declaredPep || screenedPep)
      ? `${pepWhat}${pepRole ? ' (' + pepRole + ')' : ''}. Senior management approval is needed before the relationship starts: record it, with the approver's name, with Resolve`
      : declared ? 'Declared not a politically exposed person, and not found as one by the screen' : 'No declaration recorded',
    done: declared && !declaredPep && !screenedPep,
    pepApprovalNeeded: declaredPep || screenedPep,
  });

  const occ = [real(cp.occupation), real(cp.employmentStatus), real(cp.employer)].filter(Boolean);
  checks.push({
    key: 'occupation', q: 'How does the client earn their income?', resolvable: true,
    detail: occ.length ? `${occ.join(' · ')}${real(cp.annualIncome) ? ' · income ' + real(cp.annualIncome) : ''}` : 'Not recorded',
    done: occ.length > 0,
  });

  const sof = (r.sofAssessment && typeof r.sofAssessment === 'object') ? r.sofAssessment : {};
  const ownName = sof.accountInOwnName;
  checks.push({
    key: 'funds', q: 'Where are funds coming from?', resolvable: true,
    detail: !real(sof.namedAccount) ? 'No account named'
      : ownName === false ? `${real(sof.namedAccount)}: not in the client's own name${real(cp.thirdPartyInvolvement) ? ' (' + real(cp.thirdPartyInvolvement).slice(0, 200) + ')' : ''}`
        : `${real(sof.namedAccount)}${ownName === true ? ' · in the client\'s own name' : ' · whether it is in the client\'s own name is not recorded'}${real(sof.fundingRoute) ? ' · ' + real(sof.fundingRoute).slice(0, 300) : ''}`,
    done: !!(real(sof.namedAccount) && ctx.sofSatisfactory && ownName === true),
  });

  const sow = (r.sowAssessment && typeof r.sowAssessment === 'object') ? r.sowAssessment : {};
  const sowScore = real(sow.score);
  checks.push({
    key: 'wealth', q: 'How was the client\'s wealth built?', resolvable: true,
    detail: sowScore ? `${sowScore}${real(sow.origins) ? ' · ' + real(sow.origins).slice(0, 300) : real(sow.narrative) ? ' · ' + real(sow.narrative).slice(0, 300) : ''}` : 'Not assessed',
    done: /^(satisfactory|basic|accepted by mlro)/i.test(sowScore),
  });

  checks.push({
    key: 'ownbehalf', q: 'Is the client acting for themselves?', resolvable: true,
    detail: cp.actingForThemselves === true ? 'Confirmed: their own account and their own money' : cp.actingForThemselves === false ? `No: ${real(cp.thirdPartyInvolvement) || 'someone else is involved'}` : 'Not confirmed',
    done: cp.actingForThemselves === true,
  });

  const rule = (r.individualDocumentRule && typeof r.individualDocumentRule === 'object') ? r.individualDocumentRule : null;
  const th = rule && rule.thresholds ? rule.thresholds : null;
  const BAND = { none: 'no money document needed', sof_document: 'one source of funds document', full: 'full source of funds and source of wealth documents', undetermined: 'the amount could not be compared' };
  const SRC = { policy: 'the firm\'s policy', default: 'the platform defaults', policy_invalid: 'the firm\'s policy, which is not consistent (the stricter reading was taken)', firm_unread: 'the platform defaults, because the firm\'s settings could not be read' };
  checks.push({
    key: 'docrule', q: 'Documents for the money (the firm\'s thresholds)', resolvable: true,
    detail: !rule ? 'The documents rule has not run on this board: Regenerate'
      : `${BAND[rule.band] || rule.band}${rule.basis ? ' (' + rule.basis + ')' : ''}${th ? `; thresholds ${th.currency} ${th.sofDocument} and ${th.fullSofSow} from ${SRC[th.source] || th.source}` : ''}`,
    done: !!rule && rule.band !== 'undetermined' && !!th && !['policy_invalid', 'firm_unread'].includes(th.source),
  });

  const formAState = ctx.formAState;
  const sumsubOff = (ctx.features || {}).sumsub === false;
  if (formAState !== 'not_required' && sumsubOff) {
    // Review finding S4: Form A is signed in Sumsub; with identity verification
    // switched off the requirement stays, open, and says why.
    checks.push({ key: 'formA', q: 'Form A signed by the client', resolvable: formAState !== 'required', detail: 'Form A is ' + (formAState === 'required' ? 'required' : 'possibly required (the case\'s regime is not known)') + ', but identity verification (Sumsub), where it is signed, is switched off for this firm', done: false });
  } else if (formAState !== 'not_required') {
    const read = Array.isArray(ctx.formRequests);
    const confirmed = read && clientFormAConfirmed(ctx.formRequests, name, ctx.samePerson);
    const sent = read && ctx.formRequests.some((f) => f && f.form_type === 'A' && !['cancelled', 'superseded'].includes(String(f.status || '').toLowerCase()));
    checks.push({
      key: 'formA', q: 'Form A signed by the client',
      resolvable: formAState !== 'required',
      detail: !read ? 'The Form A status is not available on this view'
        : (ctx.formRequestsPartial && !confirmed) ? 'Not confirmed in Sumsub, and the legacy DeepSign forms could not be read, so a form signed there is not counted'
        : confirmed ? 'Confirmed by the client'
          : `${sent ? 'Sent, not yet confirmed' : 'Not sent'}${formAState === 'required' ? ' (required: individual client under the Swiss regime)' : ' (the case\'s regime is not known: resolve with a reason if Form A is not required)'}`,
      done: !!confirmed,
    });
  }

  applyCheckResolutions(checks, ctx.checkRes || {});
  return lockUnresolvable(checks);
}

/**
 * What no resolution clears on an individual's file, whatever later pass
 * applies resolutions: Form A under the Swiss regime; the identity check and
 * the sanctions check (review note N1); and a politically exposed person whose
 * resolution is not a recorded approval (review finding S1: a resolution given
 * before the finding, for example on "No declaration recorded", does not stand
 * for an approval it never was).
 */
export function lockUnresolvable(checks) {
  for (const c of (Array.isArray(checks) ? checks : [])) {
    if (!c || !c.officerResolved) continue;
    const locked = (c.key === 'formA' && c.resolvable === false) || c.key === 'sumsub' || c.key === 'sanctions';
    const pepNotApproval = c.key === 'pep' && c.pepApprovalNeeded && !(c.officerResolved.pepApproval && String(c.officerResolved.pepApproval.approver || '').trim());
    if (locked || pepNotApproval) {
      c.done = false;
      if (pepNotApproval) c.detail += '. A resolution recorded without a named approval does not stand for one';
      delete c.officerResolved;
    }
  }
  return checks;
}

/**
 * THE HARD BLOCKS AN INDIVIDUAL'S APPROVAL CANNOT OVERRIDE (review of pieces 8
 * and 9, finding B1). An override (a reason and an approver) clears ordinary
 * open items; these it never clears: the client's own identity verified, the
 * client screened as a person, Form A confirmed under the Swiss regime, and a
 * politically exposed person relationship approved by name. cs: the case state;
 * identityOutstanding: the identity set's outstanding people.
 */
export function individualSignoffHardBlocks(cs, identityOutstanding, clientName, samePerson) {
  const same = typeof samePerson === 'function' ? samePerson : ((a, b) => a === b);
  const all = [...((cs && cs.checks) || []), ...((cs && cs.procedural) || [])];
  const k = (key) => all.find((c) => c && c.key === key);
  const out = [];
  const idc = k('sumsub');
  const clientOpen = (identityOutstanding || []).some((p) => p && clientName && same(p.name, clientName));
  if (!idc) out.push({ key: 'client_identity', text: 'The client\'s identity verification (Sumsub) is switched off for this firm, so the client has not been verified. An individual client is verified before approval; this cannot be overridden.' });
  else if (clientOpen) out.push({ key: 'client_identity', text: 'The client\'s identity verification is not complete. An individual client is verified before approval; this cannot be overridden.' });
  const sc = k('sanctions');
  if (!sc) out.push({ key: 'client_screening', text: 'Sanctions and politically exposed person screening is switched off for this firm, so the client has not been screened as a person. This cannot be overridden.' });
  else if (!sc.done) out.push({ key: 'client_screening', text: `The client's screening is not clear (${sc.detail}). This cannot be overridden.` });
  const fa = k('formA');
  if (fa && !fa.done && fa.resolvable === false) out.push({ key: 'form_a', text: `Form A is not confirmed by the client (${fa.detail}). An individual client under the Swiss regime signs Form A before approval; this cannot be overridden.` });
  const pep = k('pep');
  if (pep && pep.pepApprovalNeeded && !pep.done) out.push({ key: 'pep_approval', text: 'The client is a politically exposed person and no senior management approval is recorded. Record it with Resolve on the check, naming the approver; this cannot be overridden.' });
  return out;
}

/**
 * HIGH RISK INDIVIDUAL: THE MLRO APPROVES (owner, 21/10/2026). An approval of
 * an individual client at High risk records the MLRO's approval by name. When
 * the firm's policy names an MLRO or a deputy, the name must be one of them;
 * when it names neither, any name is taken and the record says the policy names
 * no MLRO. Returns null (no block), or { key, text } for the sign off gate's
 * hard blocks. Never applies to a company case.
 */
// Review finding S3: High in either place the tier is held, in any case.
// Model tier pending (29/10/2026): the tier in force on the report, never the model's riskTier.
export const isHighTier = (report, sessionTier) => [tierInForce(report || {}), sessionTier].some((t) => String(t || '').trim().toLowerCase() === 'high');

export function individualMlroApprovalBlock({ report, collected, decision, mlroApproval, policyOfficers, samePerson, sessionTier } = {}) {
  const cd = (collected && typeof collected === 'object') ? collected : {};
  if (String(cd.clientKind || '').toLowerCase() !== 'individual' || decision !== 'Approve') return null;
  if (!isHighTier(report, sessionTier)) return null;
  const same = typeof samePerson === 'function' ? samePerson : ((a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase());
  const given = String(mlroApproval == null ? '' : mlroApproval).replace(/\s+/g, ' ').trim();
  const po = (policyOfficers && typeof policyOfficers === 'object') ? policyOfficers : {};
  const named = [po.mlro, po.deputy].map((x) => String(x || '').trim()).filter(Boolean);
  if (given.length < 3) return { key: 'mlro_approval', text: `This is a high risk individual client, so the MLRO approves the relationship. Enter the name of the MLRO approving it${named.length ? ' (' + named.join(' or ') + ')' : ''}.` };
  if (named.length && !named.some((n) => same(n, given))) return { key: 'mlro_approval', text: `${given} is not the MLRO or deputy named in the firm's policy (${named.join(' or ')}). A high risk individual client is approved by one of them.` };
  return null;
}

/** What the decision record stores for that approval. */
export function mlroApprovalRecord({ mlroApproval, policyOfficers, recordedBy, at }) {
  const po = (policyOfficers && typeof policyOfficers === 'object') ? policyOfficers : {};
  const named = !!(String(po.mlro || '').trim() || String(po.deputy || '').trim());
  return { name: String(mlroApproval).replace(/\s+/g, ' ').trim().slice(0, 200), basis: named ? 'named in the firm\'s policy' : 'the firm\'s policy names no MLRO', recordedBy: recordedBy || null, at: at || null };
}
